import * as screeningActions from './screeningReducer';
import Axios from 'axios';
import { toastr } from 'react-redux-toastr';
import confirm from 'react-confirm2';

const INCLUDED =
  '?include=conditions,conditions.questions.options,conditions.questions.selected';

const setIndex = (index) => {
  return {
    type: screeningActions.SET_INDEX,
    index,
  };
};
const isSubmitting = (status) => {
  return {
    type: screeningActions.IS_SUBMITTING,
    status,
  };
};
const isRetrievingQuestion = (status) => {
  return {
    type: screeningActions.IS_RETRIEVING_QUESTION,
    status,
  };
};
const isCancelling = (status) => {
  return {
    type: screeningActions.IS_CANCELLING,
    status,
  };
};

const isRecalculatingScore = (status) => {
  return {
    type: screeningActions.IS_RECALCULATING_SCORE,
    status,
  };
};
const isEditing = (status) => {
  return {
    type: screeningActions.IS_EDITING,
    status,
  };
};
const isDeleting = (status) => {
  return {
    type: screeningActions.IS_DELETING,
    status,
  };
};
const isSearching = (status) => {
  return {
    type: screeningActions.IS_SEARCHING,
    status,
  };
};
const startScreeningSession = (
  region,
  destinations,
  id,
  service,
  clearScreening,
  dob,
) => {
  const values = {
    region,
    destinations,
    id,
    dob,
  };

  if (clearScreening) {
    values.clear_existing_screening = true;
  }

  return Axios.post(`/public/medical/${service}:start`, {
    meta: values,
  });
};

const finishHealixSession = (id, service) => {
  return Axios.post(`/public/medical/${service}:finish`, {
    meta: {
      id,
    },
  });
};

const processScreening = (screeningId, service, sessionId) => {
  return (dispatch) => {
    if (screeningId) {
      return Axios.get(`/public/medical/${service}/${sessionId}${INCLUDED}`).then(
        (res) => {
          dispatch({
            type: screeningActions.START_SCREENING,
            token: sessionId,
            conditions: res.data,
            service,
          });

          return true;
        },
      );
    }

    dispatch({
      type: screeningActions.START_SCREENING,
      token: sessionId,
      conditions: {},
      service,
    });
  };
};

export const startScreening = (
  travellerIndex,
  screeningId,
  region,
  destinations = [],
  service = 'healix',
  clearScreening,
  dob,
) => {
  return (dispatch) => {
    dispatch(isSubmitting(true));
    return startScreeningSession(
      region,
      destinations,
      screeningId,
      service,
      clearScreening,
      dob,
    )
      .then((sessionResponse) => {
        dispatch(setIndex(travellerIndex));
        dispatch(processScreening(screeningId, service, sessionResponse.data.meta.id));

        return true;
      })
      .finally(() => {
        dispatch(isSubmitting(false));
      });
  };
};

const recalculateScore = (callback, screeningId, region, destinations = [], service) => {
  return (dispatch) => {
    dispatch(isRecalculatingScore(true));
    return Axios.post(`/public/medical/${service}:recalculate`, {
      meta: {
        region,
        destinations,
        id: screeningId,
      },
    })
      .then((res) => {
        if (callback) {
          callback(
            res.data.meta.conditions.length ? res.data : '', // screening payload
            res.data.meta.conditions.length ? screeningId : '', // screening id
          );
        }
        dispatch(isRecalculatingScore(false));
        return {
          screening: res.data.meta.conditions.length ? res.data : '', // screening payload
          screening_id: res.data.meta.conditions.length ? screeningId : '', // screening id
        };
      })
      .finally(() => {
        dispatch(isRecalculatingScore(false));
      });
  };
};

const finishScreening = (callback) => {
  return (dispatch, getState) => {
    dispatch(isSubmitting(true));
    const state = getState().screening;
    return finishHealixSession(state.token, state.service)
      .then((res) => {
        dispatch({
          type: screeningActions.FINISH_SCREENING,
          data: res.data,
        });
        if (callback) {
          callback(
            res.data.meta.conditions.length ? res.data : '',
            res.data.meta.conditions.length ? state.token : '',
          );
        }
        return {
          screening: res.data.meta.conditions.length ? res.data : '',
          screening_id: res.data.meta.conditions.length ? state.token : '',
        };
      })
      .catch((err) => {
        dispatch({
          type: screeningActions.SET_FINISH_SCREENING_ERROR,
          data: err?.response?.data?.errors?.[0]?.detail ?? 'unknown error',
        });
      })
      .finally(() => {
        dispatch(isSubmitting(false));
      });
  };
};

const findCondition = (search) => {
  return (dispatch, getState) => {
    const state = getState().screening;

    if (!search.length || search.length < 2) {
      return null;
    }

    const service = state.service;
    dispatch(isSearching(true));

    Axios.get(
      `/public/medical/${service}/${
        state.token
      }/conditions?filter[name]=${encodeURIComponent(search)}`,
    )
      .then((res) => {
        const options = res.data.data.attributes.conditions
          .map((condition) => {
            return {
              value: condition.id,
              label: condition.name,
            };
          });
        const existingCondition = res.data.data.attributes.existing_condition;
        if (existingCondition?.id) {
          options.push({
            value: existingCondition.id,
            label: `${existingCondition.name} already declared as ${existingCondition.existingName}`,
            isDisabled: true
          });
        }

        dispatch(isSearching(false));

        dispatch({
          type: screeningActions.FIND_CONDITION,
          data: options,
          hasMisspelledConditions: res.data.data.attributes.has_misspelled_conditions,
          hasSurgicalProcedure: res.data.data.attributes.has_surgical_procedure,
        });
        return true;
      })
      .catch((err) => {
        dispatch(isSearching(false));
        console.error(err);
      });
  };
};

const startDeclaration = (condition) => {
  return (dispatch, getState) => {
    const state = getState().screening;

    const service = getState().screening.service;
    dispatch(isSubmitting(true));

    return Axios.post(
      `/public/medical/${service}/${state.token}/declaration${INCLUDED}`,
      {
        id: condition.value,
        name: condition.label,
      },
    )
      .then((res) => {
        dispatch({
          type: screeningActions.START_DECLARATION,
          index: 0,
          declaration: res.data,
        });

        dispatch(isSubmitting(false));
        return true;
      })
      .catch((err) => {
        const message = err?.response?.data?.errors?.[0]?.detail ?? 'unknown error';
        toastr.error('Error', message);
        dispatch(isSubmitting(false));
      });
  };
};

const editDeclaration = (declarationId) => {
  return (dispatch, getState) => {
    const state = getState().screening;
    dispatch(isEditing(declarationId));
    const service = getState().screening.service;
    return Axios.post(
      `/public/medical/${service}/${state.token}/declaration${INCLUDED}`,
      {
        id: declarationId,
      },
    )
      .then((res) => {
        dispatch({
          type: screeningActions.START_DECLARATION,
          index: 0,
          declaration: res.data,
        });

        dispatch(isEditing(false));
        return true;
      })
      .catch((err) => {
        dispatch(isEditing(false));
        console.error(err);
      });
  };
};

const cancelDeclaration = () => {
  return (dispatch, getState) => {
    const state = getState().screening;
    dispatch(isCancelling(true));
    const service = getState().screening.service;
    return Axios.delete(`/public/medical/${service}/${state.token}/declaration${INCLUDED}`, {
      finish: true,
    })
      .then(() => {
        return Axios.get(`/public/medical/${service}/${state.token}${INCLUDED}`)
          .then((res) => {
            dispatch({
              type: screeningActions.FINISH_DECLARATION,
              conditions: res.data,
            });

            return true;
          })
          .catch((err) => {
            console.error(err);
          });
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        dispatch(isCancelling(false));
      });
  };
};

const finishDeclaration = () => {
  return (dispatch, getState) => {
    const state = getState().screening;
    dispatch(isSubmitting(true));
    const service = getState().screening.service;
    return Axios.patch(
      `/public/medical/${service}/${state.token}/declaration${INCLUDED}`,
      {
        finish: true,
      },
    )
      .then(() => {
        return Axios.get(`/public/medical/${service}/${state.token}${INCLUDED}`)
          .then((res) => {
            dispatch({
              type: screeningActions.FINISH_DECLARATION,
              conditions: res.data,
            });
            dispatch(isSubmitting(false));
            return true;
          })
          .catch((err) => {
            dispatch(isSubmitting(false));
            console.error(err);
          });
      })
      .catch((err) => {
        dispatch(isSubmitting(false));
        console.error(err);
      });
  };
};

const deleteDeclaration = (declarationId, name) => {
  return (dispatch, getState) => {
    const state = getState().screening;
    dispatch(isDeleting(declarationId));
    const service = getState().screening.service;
    return new Promise((resolve, reject) => {
      confirm(
        'Remove Medical Condition?',
        {
          description: `You have requested to remove ${name} from your medical declaration. This will also remove any previously declared conditions which are medically linked. Please ensure you check your declared conditions before proceeding`,
          close: () => {
            dispatch(isDeleting(false));

            // Resolve
            resolve();
          },
          done: () => {
            Axios.delete(
              `/public/medical/${service}/${
                state.token
              }/conditions/${declarationId}${INCLUDED}`,
              {
                id: declarationId,
              },
            )
              .then(() => {
                Axios.get(`/public/medical/${service}/${state.token}${INCLUDED}`)
                  .then((res) => {
                    dispatch({
                      type: screeningActions.DELETE_DECLARATION,
                      conditions: res.data,
                    });
                    dispatch(isDeleting(false));

                    // Resolve
                    resolve(res);
                  })
                  .catch((err) => {
                    dispatch(isDeleting(false));
                    console.error(err);

                    // Reject
                    reject(err);
                  });
              })
              .catch((err) => {
                dispatch(isDeleting(false));
                console.error(err);

                //Reject
                reject(err);
              });
          },
        },
      );
    });
  };
};

const setAnswer = (conditionNumber, questionNumber, answer) => {
  return (dispatch, getState) => {
    const state = getState().screening;
    dispatch(isSubmitting(true));
    const service = getState().screening.service;
    return Axios.patch(
      `/public/medical/${service}/${
        state.token
      }/declaration/conditions/${conditionNumber}/questions/${questionNumber}${INCLUDED}`,
      answer,
    )
      .then((res) => {
        dispatch({
          type: screeningActions.SET_ANSWER,
          declaration: res.data,
        });

        dispatch(isSubmitting(false));
        return true;
      })
      .catch((err) => {
        dispatch(isSubmitting(false));
        console.error(err);
      });
  };
};

const editAnswer = (declarationNumber, conditionNumber, questionNumber) => {
  return (dispatch, getState) => {
    const state = getState().screening;
    const service = getState().screening.service;
    dispatch(isRetrievingQuestion(true));
    return Axios.post(
      `/public/medical/${service}/${
        state.token
      }/declaration/${declarationNumber}/conditions/${conditionNumber}/questions/${questionNumber}${INCLUDED}`,
    )
      .then((res) => {
        dispatch({
          type: screeningActions.SET_ANSWER,
          declaration: res.data,
        });

        return true;
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        dispatch(isRetrievingQuestion(false));
      });
  };
};

const reset = () => {
  return {
    type: screeningActions.RESET,
  };
};

export const actions = {
  setIndex,
  isSubmitting,
  isRecalculatingScore,
  isEditing,
  isDeleting,
  isSearching,
  startHealixSession: startScreeningSession,
  finishHealixSession,
  startScreening,
  recalculateScore,
  finishScreening,
  findCondition,
  startDeclaration,
  editDeclaration,
  cancelDeclaration,
  finishDeclaration,
  deleteDeclaration,
  setAnswer,
  editAnswer,
  reset,
};
